import React, { useState, useMemo } from "react";
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
	LineChart,
	Line,
	ReferenceLine,
} from "recharts";
import axiosClient from "../../axios";
import DayWiseTable from "./DayWiseTable";
import OffersEngagementChart, { DAYS } from "./OffersEngagementChart";
import {
	aggregateOfferCategoriesFromCohorts,
	aggregateSurveyFromCohorts,
} from "./cohortAggregateUtils";

// ─── Colours ─────────────────────────────────────────────────────────────────
const COLOR_A = "#3B82F6";
const COLOR_B = "#8B5CF6";

// ─── Metric rows for comparison table ────────────────────────────────────────
const METRICS = [
	{
		label: "Total Users",
		aVal: (a) => a.total_users?.toLocaleString() ?? "—",
		bVal: (b) => b.total_users?.toLocaleString() ?? "—",
		deltaKey: "total_users_diff",
		fmt: (v) => v,
	},
	{
		label: "Avg Users / Day",
		aVal: (a) => a.avg_users_per_day?.toFixed(1) ?? "—",
		bVal: (b) => b.avg_users_per_day?.toFixed(1) ?? "—",
		deltaKey: "avg_users_diff",
		fmt: (v) => v,
	},
	{
		label: "D0 Activation %",
		aVal: (a) => a.activation?.d0_rate != null ? `${a.activation.d0_rate}%` : "—",
		bVal: (b) => b.activation?.d0_rate != null ? `${b.activation.d0_rate}%` : "—",
		deltaKey: "activation_d0_diff",
		fmt: (v) => `${v}%`,
	},
	{
		label: "D7 Activation %",
		aVal: (a) => a.activation?.d7_rate != null ? `${a.activation.d7_rate}%` : "—",
		bVal: (b) => b.activation?.d7_rate != null ? `${b.activation.d7_rate}%` : "—",
		deltaKey: "activation_d7_diff",
		fmt: (v) => `${v}%`,
	},
	{
		label: "D15 Activation %",
		aVal: (a) => a.activation?.d15_rate != null ? `${a.activation.d15_rate}%` : "—",
		bVal: (b) => b.activation?.d15_rate != null ? `${b.activation.d15_rate}%` : "—",
		deltaKey: "activation_d15_diff",
		fmt: (v) => `${v}%`,
	},
	{
		label: "D15 Revenue",
		aVal: (a) => a.revenue?.d15_avg != null ? `$${(a.revenue.d15_avg / 1000).toFixed(2)}` : "—",
		bVal: (b) => b.revenue?.d15_avg != null ? `$${(b.revenue.d15_avg / 1000).toFixed(2)}` : "—",
		deltaKey: "revenue_d15_diff",
		fmt: (v) => `$${(parseFloat(v) / 1000).toFixed(2)}`,
	},
	{
		label: "D15 Payout",
		aVal: (a) => a.payout?.d15_avg != null ? `$${(a.payout.d15_avg / 1000).toFixed(2)}` : "—",
		bVal: (b) => b.payout?.d15_avg != null ? `$${(b.payout.d15_avg / 1000).toFixed(2)}` : "—",
		deltaKey: "payout_d15_diff",
		fmt: (v) => `$${(parseFloat(v) / 1000).toFixed(2)}`,
	},
	{
		label: "D15 Withdrawals (users)",
		aVal: (a) => a.withdrawals?.d15_users_avg?.toFixed(1) ?? "—",
		bVal: (b) => b.withdrawals?.d15_users_avg?.toFixed(1) ?? "—",
		deltaKey: "withdrawal_d15_diff",
		fmt: (v) => v,
	},
];

// ─── Delta badge ─────────────────────────────────────────────────────────────
const DeltaCell = ({ raw, fmt }) => {
	if (raw == null || raw === undefined) return <td style={s.td}>—</td>;
	const num = parseFloat(raw);
	if (isNaN(num)) return <td style={s.td}>—</td>;
	if (num > 0)
		return (
			<td style={{ ...s.td, color: "#16A34A", fontWeight: 600 }}>
				▲ {fmt ? fmt(raw) : raw}
			</td>
		);
	if (num < 0)
		return (
			<td style={{ ...s.td, color: "#DC2626", fontWeight: 600 }}>
				▼ {fmt ? fmt(raw) : raw}
			</td>
		);
	return <td style={{ ...s.td, color: "#9CA3AF" }}>–</td>;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const today = new Date().toISOString().split("T")[0];

const GroupBadge = ({ letter, color }) => (
	<span
		style={{
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			width: 22,
			height: 22,
			borderRadius: "50%",
			background: color,
			color: "#fff",
			fontSize: 11,
			fontWeight: 700,
			flexShrink: 0,
		}}
	>
		{letter}
	</span>
);

// ─── Cohort date dropdown + DayWiseTable ─────────────────────────────────────
const CohortDayWise = ({ group, groupLabel, color, partners, totalUsers }) => {
	const cohorts = group?.cohorts ?? [];
	const [selectedIdx, setSelectedIdx] = useState(0);

	if (!cohorts.length) return <p style={s.noData}>No cohorts available.</p>;

	const selected = cohorts[selectedIdx];
	const dayWise = selected?.day_wise_data;

	return (
		<div>
			{/* Cohort selector */}
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: 12,
					marginBottom: 16,
					flexWrap: "wrap",
				}}
			>
				<GroupBadge letter={groupLabel} color={color} />
				<span style={{ fontWeight: 600, color: "var(--text-primary)", fontSize: 14 }}>
					Group {groupLabel}
				</span>
				<span style={{ color: "var(--text-muted)", fontSize: 13 }}>
					{cohorts.length} cohort{cohorts.length !== 1 ? "s" : ""} in range — viewing:
				</span>
				<select
					value={selectedIdx}
					onChange={(e) => setSelectedIdx(Number(e.target.value))}
					style={s.select}
				>
					{cohorts.map((c, i) => (
						<option key={i} value={i}>
							{c.cohort_date} &nbsp;({c.total_users?.toLocaleString()} users)
						</option>
					))}
				</select>
			</div>

			{/* Info banner */}
			<div
				style={{
					background: color + "10",
					border: `1px solid ${color}30`,
					borderRadius: 8,
					padding: "10px 14px",
					marginBottom: 16,
					display: "flex",
					gap: 24,
					flexWrap: "wrap",
					fontSize: 13,
				}}
			>
				<span>
					<strong style={{ color }}>Cohort date:</strong>{" "}
					{selected.cohort_date}
				</span>
				<span>
					<strong style={{ color }}>Registered users:</strong>{" "}
					{selected.total_users?.toLocaleString()}
				</span>
			</div>

			{dayWise?.length ? (
				<DayWiseTable
					dayWiseData={dayWise}
					partners={partners}
					totalUsers={selected.total_users ?? totalUsers}
				/>
			) : (
				<p style={s.noData}>No day-wise data for this cohort.</p>
			)}
		</div>
	);
};

// ─── Offer category comparison ────────────────────────────────────────────────
const OfferCategoryComparison = ({ cohortsA, cohortsB }) => {
	const catsA = useMemo(() => aggregateOfferCategoriesFromCohorts(cohortsA), [cohortsA]);
	const catsB = useMemo(() => aggregateOfferCategoriesFromCohorts(cohortsB), [cohortsB]);

	if (!catsA.length && !catsB.length) return null;

	// Merge all category names from both groups
	const allCategories = Array.from(
		new Set([...catsA.map((c) => c.category), ...catsB.map((c) => c.category)])
	);

	const mapA = Object.fromEntries(catsA.map((c) => [c.category, c]));
	const mapB = Object.fromEntries(catsB.map((c) => [c.category, c]));

	const rows = allCategories
		.map((cat) => {
			const a = mapA[cat] ?? { avg_clicks: 0, avg_completed: 0, avg_users: 0 };
			const b = mapB[cat] ?? { avg_clicks: 0, avg_completed: 0, avg_users: 0 };
			return { cat, a, b, totalClicks: (a.avg_clicks ?? 0) + (b.avg_clicks ?? 0) };
		})
		.sort((x, y) => y.totalClicks - x.totalClicks);

	const BetterCell = ({ aClicks, bClicks }) => {
		if (aClicks > bClicks)
			return <td style={{ ...s.td, color: "#16A34A", fontWeight: 700, textAlign: "center" }}>A ▲</td>;
		if (bClicks > aClicks)
			return <td style={{ ...s.td, color: "#7C3AED", fontWeight: 700, textAlign: "center" }}>B ▲</td>;
		return <td style={{ ...s.td, color: "#9CA3AF", textAlign: "center" }}>–</td>;
	};

	return (
		<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
			<h3 style={s.sectionTitle}>📦 Offer Category Comparison</h3>

			{/* Calculation explanation */}
			<div style={{
				background: "#F0F9FF",
				border: "1px solid #BAE6FD",
				borderRadius: 8,
				padding: "10px 14px",
				marginBottom: 16,
				fontSize: 13,
				color: "#0369A1",
				lineHeight: 1.6,
			}}>
				<strong>How is this calculated?</strong> Each value is the average per cohort day within the group's date range.
				For example, if Group A spans 7 days and had 1,400 total clicks on Gaming across all cohorts,
				<em> Clicks/Day = 1,400 ÷ 7 = 200</em>. This normalises groups with different date ranges so they're directly comparable.
				Rows are sorted by combined click volume (A + B) — highest engagement first.
			</div>

			<div style={{ overflowX: "auto" }}>
				<table style={s.table}>
					<thead>
						<tr>
							<th style={s.th}>Category</th>
							<th style={{ ...s.th, color: COLOR_A }}>A · Clicks/Day</th>
							<th style={{ ...s.th, color: COLOR_A }}>A · Completed/Day</th>
							<th style={{ ...s.th, color: COLOR_A }}>A · Users/Day</th>
							<th style={{ ...s.th, color: COLOR_B }}>B · Clicks/Day</th>
							<th style={{ ...s.th, color: COLOR_B }}>B · Completed/Day</th>
							<th style={{ ...s.th, color: COLOR_B }}>B · Users/Day</th>
							<th style={{ ...s.th, textAlign: "center" }}>Better</th>
						</tr>
					</thead>
					<tbody>
						{rows.map(({ cat, a, b }, i) => (
							<tr key={cat} style={i % 2 === 0 ? s.rowEven : s.rowOdd}>
								<td style={{ ...s.td, fontWeight: 500, color: "var(--text-primary)" }}>{cat}</td>
								<td style={s.td}>{a.avg_clicks?.toLocaleString() ?? "—"}</td>
								<td style={s.td}>{a.avg_completed?.toLocaleString() ?? "—"}</td>
								<td style={s.td}>{a.avg_users?.toLocaleString() ?? "—"}</td>
								<td style={s.td}>{b.avg_clicks?.toLocaleString() ?? "—"}</td>
								<td style={s.td}>{b.avg_completed?.toLocaleString() ?? "—"}</td>
								<td style={s.td}>{b.avg_users?.toLocaleString() ?? "—"}</td>
								<BetterCell aClicks={a.avg_clicks ?? 0} bClicks={b.avg_clicks ?? 0} />
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
};

// ─── Offers engagement comparison (trend chart + day drill-down) ────────────
// `cohorts` is the raw per-date cohort array for a group (each has its own
// `summary.offers_engagement_by_day` and `day_wise_data[i].offers`).
// The backend's aggregateGroup() doesn't average this across cohorts yet, so
// it's aggregated here on the client — same pattern as CohortTimeline below.
const aggregateEngagementByDay = (cohorts) => {
	return DAYS.map((day) => {
		const rows = (cohorts ?? [])
			.map((c) => c.summary?.offers_engagement_by_day?.find((d) => d.day === day))
			.filter(Boolean);
		if (!rows.length) return { day, avg_clicks_per_user: "0.00", avg_completed_per_user: "0.00" };
		const avgClicks = rows.reduce((s, r) => s + (parseFloat(r.avg_clicks_per_user) || 0), 0) / rows.length;
		const avgCompleted = rows.reduce((s, r) => s + (parseFloat(r.avg_completed_per_user) || 0), 0) / rows.length;
		return { day, avg_clicks_per_user: avgClicks.toFixed(2), avg_completed_per_user: avgCompleted.toFixed(2) };
	});
};

const aggregateDayOffers = (cohorts, day) => {
	const catMap = {};
	const offerMap = {};

	(cohorts ?? []).forEach((c) => {
		const dayData = c.day_wise_data?.find((d) => d.day === day);

		(dayData?.offers?.categories ?? []).forEach((cat) => {
			const key = cat.category;
			if (!catMap[key]) {
				catMap[key] = { category: key, total_clicks: 0, completed_clicks: 0, total_payout: 0 };
			}
			catMap[key].total_clicks += cat.total_clicks || 0;
			catMap[key].completed_clicks += cat.completed_clicks || 0;
			catMap[key].total_payout += cat.total_payout || 0;
		});

		(dayData?.offers?.top_offers ?? []).forEach((o) => {
			const key = o.offerId ?? o._id;
			if (!offerMap[key]) {
				offerMap[key] = { offerId: key, offer_name: o.offer_name, total_clicks: 0, total_payout: 0, completed_clicks: 0, total_users: 0 };
			}
			offerMap[key].total_clicks += o.total_clicks || 0;
			offerMap[key].total_payout += o.total_payout || 0;
			offerMap[key].completed_clicks += o.completed_clicks || 0;
			offerMap[key].total_users += o.total_users || 0;
		});
	});

	return {
		categories: Object.values(catMap).sort((a, b) => b.total_clicks - a.total_clicks),
		topOffers: Object.values(offerMap)
			.sort((a, b) => b.total_clicks - a.total_clicks)
			.slice(0, 15),
	};
};

const OffersEngagementComparison = ({ cohortsA, cohortsB }) => {
	const engA = useMemo(() => aggregateEngagementByDay(cohortsA), [cohortsA]);
	const engB = useMemo(() => aggregateEngagementByDay(cohortsB), [cohortsB]);

	if (!cohortsA?.length && !cohortsB?.length) return null;

	return (
		<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
			<h3 style={s.sectionTitle}>📈 Offers Engagement Comparison (D0-D15)</h3>
			<OffersEngagementChart
				seriesA={{ label: "Group A", color: COLOR_A, data: engA }}
				seriesB={{ label: "Group B", color: COLOR_B, data: engB }}
				getDetailA={(day) => aggregateDayOffers(cohortsA, day)}
				getDetailB={(day) => aggregateDayOffers(cohortsB, day)}
			/>
		</div>
	);
};

// ─── Survey funnel comparison ─────────────────────────────────────────────────
const SURVEY_TYPES = [
	{ key: "registration_profiler", label: "Registration Profiler" },
	{ key: "other_profiler",        label: "Other Profiler" },
	{ key: "partner_survey",        label: "Partner Survey" },
];

const rateStyle = (rate) => {
	const r = parseFloat(rate);
	if (r > 70)  return { color: "#065F46", background: "#D1FAE5", borderRadius: 12, padding: "2px 8px", fontWeight: 700, fontSize: 12 };
	if (r >= 40) return { color: "#92400E", background: "#FEF3C7", borderRadius: 12, padding: "2px 8px", fontWeight: 700, fontSize: 12 };
	return       { color: "#991B1B", background: "#FEE2E2", borderRadius: 12, padding: "2px 8px", fontWeight: 700, fontSize: 12 };
};

const SurveyFunnelComparison = ({ cohortsA, cohortsB }) => {
	const survA = useMemo(() => aggregateSurveyFromCohorts(cohortsA), [cohortsA]);
	const survB = useMemo(() => aggregateSurveyFromCohorts(cohortsB), [cohortsB]);

	const hasAny = SURVEY_TYPES.some(
		(t) => (survA?.[t.key]?.avg_attempted ?? 0) > 0 || (survB?.[t.key]?.avg_attempted ?? 0) > 0
	);
	if (!hasAny) return null;

	const BetterSurveyCell = ({ rateA, rateB }) => {
		const a = parseFloat(rateA ?? 0);
		const b = parseFloat(rateB ?? 0);
		if (a > b) return <td style={{ ...s.td, color: "#16A34A", fontWeight: 700, textAlign: "center" }}>A ▲</td>;
		if (b > a) return <td style={{ ...s.td, color: "#7C3AED", fontWeight: 700, textAlign: "center" }}>B ▲</td>;
		return <td style={{ ...s.td, color: "#9CA3AF", textAlign: "center" }}>–</td>;
	};

	return (
		<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
			<h3 style={s.sectionTitle}>📋 Survey Funnel Comparison</h3>
			<div style={{ overflowX: "auto" }}>
				<table style={s.table}>
					<thead>
						<tr>
							<th style={s.th}>Survey Type</th>
							<th style={{ ...s.th, color: COLOR_A }}>A · Attempted</th>
							<th style={{ ...s.th, color: COLOR_A }}>A · Completed</th>
							<th style={{ ...s.th, color: COLOR_A }}>A · Rate</th>
							<th style={{ ...s.th, color: COLOR_B }}>B · Attempted</th>
							<th style={{ ...s.th, color: COLOR_B }}>B · Completed</th>
							<th style={{ ...s.th, color: COLOR_B }}>B · Rate</th>
							<th style={{ ...s.th, textAlign: "center" }}>Better</th>
						</tr>
					</thead>
					<tbody>
						{SURVEY_TYPES.map(({ key, label }, i) => {
							const a = survA?.[key];
							const b = survB?.[key];
							return (
								<tr key={key} style={i % 2 === 0 ? s.rowEven : s.rowOdd}>
									<td style={{ ...s.td, fontWeight: 500, color: "var(--text-primary)" }}>{label}</td>
									<td style={s.td}>{a?.avg_attempted?.toLocaleString() ?? "—"}</td>
									<td style={s.td}>{a?.avg_completed?.toLocaleString() ?? "—"}</td>
									<td style={s.td}>
										{a?.avg_completion_rate != null
											? <span style={rateStyle(a.avg_completion_rate)}>{a.avg_completion_rate}%</span>
											: "—"}
									</td>
									<td style={s.td}>{b?.avg_attempted?.toLocaleString() ?? "—"}</td>
									<td style={s.td}>{b?.avg_completed?.toLocaleString() ?? "—"}</td>
									<td style={s.td}>
										{b?.avg_completion_rate != null
											? <span style={rateStyle(b.avg_completion_rate)}>{b.avg_completion_rate}%</span>
											: "—"}
									</td>
									<BetterSurveyCell rateA={a?.avg_completion_rate} rateB={b?.avg_completion_rate} />
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
};

// ─── Chart section ────────────────────────────────────────────────────────────
const ComparisonCharts = ({ agg_a, agg_b }) => {
	if (!agg_a || !agg_b) return null;

	// Activation bar data
	const activationData = [
		{
			name: "D0",
			"Group A": parseFloat(agg_a.activation?.d0_rate) || 0,
			"Group B": parseFloat(agg_b.activation?.d0_rate) || 0,
		},
		{
			name: "D7",
			"Group A": parseFloat(agg_a.activation?.d7_rate) || 0,
			"Group B": parseFloat(agg_b.activation?.d7_rate) || 0,
		},
		{
			name: "D15",
			"Group A": parseFloat(agg_a.activation?.d15_rate) || 0,
			"Group B": parseFloat(agg_b.activation?.d15_rate) || 0,
		},
	];

	// Revenue & Payout bar data
	const moneyData = [
		{
			name: "D15 Revenue",
			"Group A": parseFloat(((agg_a.revenue?.d15_avg ?? 0) / 1000).toFixed(2)),
			"Group B": parseFloat(((agg_b.revenue?.d15_avg ?? 0) / 1000).toFixed(2)),
		},
		{
			name: "D15 Payout",
			"Group A": parseFloat(((agg_a.payout?.d15_avg ?? 0) / 1000).toFixed(2)),
			"Group B": parseFloat(((agg_b.payout?.d15_avg ?? 0) / 1000).toFixed(2)),
		},
		{
			name: "D15 Withdrawals",
			"Group A": parseFloat((agg_a.withdrawals?.d15_users_avg ?? 0).toFixed(1)),
			"Group B": parseFloat((agg_b.withdrawals?.d15_users_avg ?? 0).toFixed(1)),
		},
	];

	const customTooltip =
		(unit = "") =>
		({ active, payload, label }) => {
			if (!active || !payload?.length) return null;
			return (
				<div
					style={{
						background: "#fff",
						border: "1px solid #E5E7EB",
						borderRadius: 8,
						padding: "10px 14px",
						fontSize: 13,
						boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
					}}
				>
					<p style={{ fontWeight: 600, marginBottom: 6, color: "#111" }}>{label}</p>
					{payload.map((p, i) => (
						<p key={i} style={{ color: p.color, margin: "3px 0" }}>
							{p.name}: <strong>{p.value}{unit}</strong>
						</p>
					))}
				</div>
			);
		};

	return (
		<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
			<h3 style={s.sectionTitle}>📈 Visual Comparison</h3>

			<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32, flexWrap: "wrap" }}>
				{/* Activation chart */}
				<div>
					<p style={s.chartLabel}>Activation Rate (%) — D0 · D7 · D15</p>
					<ResponsiveContainer width="100%" height={220}>
						<BarChart data={activationData} barCategoryGap="30%">
							<CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
							<XAxis dataKey="name" tick={{ fontSize: 12 }} />
							<YAxis unit="%" tick={{ fontSize: 12 }} />
							<Tooltip content={customTooltip("%")} />
							<Legend wrapperStyle={{ fontSize: 12 }} />
							<Bar dataKey="Group A" fill={COLOR_A} radius={[4, 4, 0, 0]} />
							<Bar dataKey="Group B" fill={COLOR_B} radius={[4, 4, 0, 0]} />
						</BarChart>
					</ResponsiveContainer>
				</div>

				{/* Revenue / Payout / Withdrawals */}
				<div>
					<p style={s.chartLabel}>Revenue ($) · Payout ($) · Withdrawals (users) — D15 avg</p>
					<ResponsiveContainer width="100%" height={220}>
						<BarChart data={moneyData} barCategoryGap="30%">
							<CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
							<XAxis dataKey="name" tick={{ fontSize: 11 }} />
							<YAxis tick={{ fontSize: 12 }} />
							<Tooltip content={customTooltip()} />
							<Legend wrapperStyle={{ fontSize: 12 }} />
							<Bar dataKey="Group A" fill={COLOR_A} radius={[4, 4, 0, 0]} />
							<Bar dataKey="Group B" fill={COLOR_B} radius={[4, 4, 0, 0]} />
						</BarChart>
					</ResponsiveContainer>
				</div>
			</div>
		</div>
	);
};

// ─── Users-per-cohort timeline ────────────────────────────────────────────────
const CohortTimeline = ({ cohortsA, cohortsB }) => {
	if (!cohortsA?.length && !cohortsB?.length) return null;

	// Align by index (Day 1 of range, Day 2, …)
	const len = Math.max(cohortsA?.length ?? 0, cohortsB?.length ?? 0);
	const data = Array.from({ length: len }, (_, i) => ({
		name: `Day ${i + 1}`,
		dateA: cohortsA?.[i]?.cohort_date ?? "",
		dateB: cohortsB?.[i]?.cohort_date ?? "",
		"Group A": cohortsA?.[i]?.total_users ?? null,
		"Group B": cohortsB?.[i]?.total_users ?? null,
	}));

	return (
		<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
			<h3 style={s.sectionTitle}>📅 Daily Registrations — Group A vs Group B</h3>
			<p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16, marginTop: -8 }}>
				Users registered per day within each group's date range (aligned by position in range)
			</p>
			<ResponsiveContainer width="100%" height={240}>
				<LineChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
					<CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
					<XAxis dataKey="name" tick={{ fontSize: 12 }} />
					<YAxis tick={{ fontSize: 12 }} />
					<Tooltip
						content={({ active, payload, label }) => {
							if (!active || !payload?.length) return null;
							const row = data.find((d) => d.name === label);
							return (
								<div
									style={{
										background: "#fff",
										border: "1px solid #E5E7EB",
										borderRadius: 8,
										padding: "10px 14px",
										fontSize: 13,
										boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
									}}
								>
									<p style={{ fontWeight: 600, marginBottom: 6, color: "#111" }}>{label}</p>
									{payload.map((p, i) => {
										const date = p.name === "Group A" ? row?.dateA : row?.dateB;
										return (
											<p key={i} style={{ color: p.color, margin: "3px 0" }}>
												{p.name}
												{date ? ` (${date})` : ""}: <strong>{p.value?.toLocaleString() ?? "—"}</strong>
											</p>
										);
									})}
								</div>
							);
						}}
					/>
					<Legend wrapperStyle={{ fontSize: 12 }} />
					<Line
						type="monotone"
						dataKey="Group A"
						stroke={COLOR_A}
						strokeWidth={2}
						dot={{ r: 4, fill: COLOR_A }}
						activeDot={{ r: 6 }}
						connectNulls
					/>
					<Line
						type="monotone"
						dataKey="Group B"
						stroke={COLOR_B}
						strokeWidth={2}
						dot={{ r: 4, fill: COLOR_B }}
						activeDot={{ r: 6 }}
						connectNulls
					/>
					<ReferenceLine y={0} stroke="#E5E7EB" />
				</LineChart>
			</ResponsiveContainer>
		</div>
	);
};

// ─── Main component ───────────────────────────────────────────────────────────
const CohortComparison = ({ partners }) => {
	const [groupA, setGroupA] = useState({ start: "", end: "" });
	const [groupB, setGroupB] = useState({ start: "", end: "" });
	const [loading, setLoading] = useState(false);
	const [result, setResult] = useState(null);
	const [error, setError] = useState(null);
	const [activeTab, setActiveTab] = useState("a");

	const canCompare = groupA.start && groupA.end && groupB.start && groupB.end;

	const handleCompare = () => {
		setError(null);
		setResult(null);
		setLoading(true);
		axiosClient
			.post("/admin/user-range-summary", {
				group_a: { start: groupA.start, end: groupA.end },
				group_b: { start: groupB.start, end: groupB.end },
			})
			.then((res) => setResult(res?.data?.response ?? res?.data ?? null))
			.catch((err) => {
				console.error("Comparison error", err);
				setError("Failed to fetch comparison data. Please try again.");
			})
			.finally(() => setLoading(false));
	};

	const agg_a = result?.group_a?.aggregated;
	const agg_b = result?.group_b?.aggregated;
	const delta = result?.delta;
	const cohortsA = result?.group_a?.cohorts;
	const cohortsB = result?.group_b?.cohorts;

	return (
		<div>
			{/* ── Date range pickers ── */}
			<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
				<h3 style={s.sectionTitle}>Select Date Ranges</h3>
				<div style={s.pickerRow}>
					{/* Group A */}
					<div style={s.pickerCard}>
						<div style={s.pickerLabel}>
							<GroupBadge letter="A" color={COLOR_A} />
							<span>Group A</span>
						</div>
						<div style={s.dateRow}>
							<div style={s.dateField}>
								<label style={s.fieldLabel}>Start Date</label>
								<input
									type="date"
									max={today}
									value={groupA.start}
									onChange={(e) => setGroupA((p) => ({ ...p, start: e.target.value }))}
									style={s.dateInput}
								/>
							</div>
							<div style={s.dateField}>
								<label style={s.fieldLabel}>End Date</label>
								<input
									type="date"
									max={today}
									min={groupA.start || undefined}
									value={groupA.end}
									onChange={(e) => setGroupA((p) => ({ ...p, end: e.target.value }))}
									style={s.dateInput}
								/>
							</div>
						</div>
					</div>

					<div style={s.vsDivider}>VS</div>

					{/* Group B */}
					<div style={s.pickerCard}>
						<div style={s.pickerLabel}>
							<GroupBadge letter="B" color={COLOR_B} />
							<span>Group B</span>
						</div>
						<div style={s.dateRow}>
							<div style={s.dateField}>
								<label style={s.fieldLabel}>Start Date</label>
								<input
									type="date"
									max={today}
									value={groupB.start}
									onChange={(e) => setGroupB((p) => ({ ...p, start: e.target.value }))}
									style={s.dateInput}
								/>
							</div>
							<div style={s.dateField}>
								<label style={s.fieldLabel}>End Date</label>
								<input
									type="date"
									max={today}
									min={groupB.start || undefined}
									value={groupB.end}
									onChange={(e) => setGroupB((p) => ({ ...p, end: e.target.value }))}
									style={s.dateInput}
								/>
							</div>
						</div>
					</div>
				</div>

				<div style={{ marginTop: "var(--spacing-lg)", textAlign: "center" }}>
					<button
						onClick={handleCompare}
						disabled={!canCompare || loading}
						style={{
							...s.compareBtn,
							opacity: !canCompare || loading ? 0.5 : 1,
							cursor: !canCompare || loading ? "not-allowed" : "pointer",
						}}
					>
						{loading ? "Comparing…" : "Compare"}
					</button>
				</div>
				{error && (
					<p style={{ color: "#DC2626", textAlign: "center", marginTop: 12 }}>{error}</p>
				)}
			</div>

			{/* ── Loading ── */}
			{loading && (
				<div className="loader-container-v4">
					<div className="loader-image-v4">📊</div>
					<p style={{ marginTop: "var(--spacing-md)", color: "var(--text-muted)" }}>
						Comparing cohorts…
					</p>
				</div>
			)}

			{result && !loading && (
				<>
					{/* ── Section 1: Comparison table ── */}
					<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
						<h3 style={s.sectionTitle}>📊 Cohort Comparison</h3>

						{/* Group summary chips */}
						<div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
							{[
								{ label: "Group A", color: COLOR_A, agg: agg_a, dates: result.group_a?.dates },
								{ label: "Group B", color: COLOR_B, agg: agg_b, dates: result.group_b?.dates },
							].map(({ label, color, agg, dates }) => (
								<div
									key={label}
									style={{
										flex: 1,
										minWidth: 200,
										background: color + "10",
										border: `1px solid ${color}30`,
										borderRadius: 8,
										padding: "10px 14px",
										display: "flex",
										gap: 16,
										alignItems: "center",
										flexWrap: "wrap",
									}}
								>
									<GroupBadge letter={label.slice(-1)} color={color} />
									<div style={{ fontSize: 13 }}>
										<span style={{ color, fontWeight: 600 }}>{label}</span>
										<span style={{ color: "var(--text-muted)", marginLeft: 6 }}>
											{dates?.start} → {dates?.end}
										</span>
									</div>
									<div style={{ fontSize: 13, marginLeft: "auto" }}>
										<strong style={{ color }}>{agg?.total_users?.toLocaleString()}</strong>
										<span style={{ color: "var(--text-muted)" }}> users</span>
									</div>
								</div>
							))}
						</div>

						<div style={{ overflowX: "auto" }}>
							<table style={s.table}>
								<thead>
									<tr>
										<th style={s.th}>Metric</th>
										<th style={{ ...s.th, color: COLOR_A }}>
											<GroupBadge letter="A" color={COLOR_A} />
											<span style={{ marginLeft: 6 }}>Group A</span>
										</th>
										<th style={{ ...s.th, color: COLOR_B }}>
											<GroupBadge letter="B" color={COLOR_B} />
											<span style={{ marginLeft: 6 }}>Group B</span>
										</th>
										<th style={{ ...s.th, textAlign: "center" }}>Delta (B − A)</th>
									</tr>
								</thead>
								<tbody>
									{METRICS.map((m, i) => (
										<tr key={i} style={i % 2 === 0 ? s.rowEven : s.rowOdd}>
											<td style={{ ...s.td, fontWeight: 500, color: "var(--text-primary)" }}>
												{m.label}
											</td>
											<td style={s.td}>{agg_a ? m.aVal(agg_a) : "—"}</td>
											<td style={s.td}>{agg_b ? m.bVal(agg_b) : "—"}</td>
											<DeltaCell
												raw={delta ? delta[m.deltaKey] : null}
												fmt={m.fmt}
											/>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>

					{/* ── Section 2: Offer category comparison ── */}
					<OfferCategoryComparison cohortsA={cohortsA} cohortsB={cohortsB} />

					{/* ── Section 2b: Offers engagement trend + drill-down ── */}
					<OffersEngagementComparison cohortsA={cohortsA} cohortsB={cohortsB} />

					{/* ── Section 3: Charts ── */}
					<ComparisonCharts agg_a={agg_a} agg_b={agg_b} />

					{/* ── Section 3: Daily registrations timeline ── */}
					<CohortTimeline cohortsA={cohortsA} cohortsB={cohortsB} />

					{/* ── Section 4: Survey funnel comparison ── */}
					<SurveyFunnelComparison cohortsA={cohortsA} cohortsB={cohortsB} />

					{/* ── Section 5: AI Analysis ── */}
					{result?.ai_analysis && (
						<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
							<h3 style={s.sectionTitle}>🤖 AI Analysis</h3>
							<div style={s.aiBox}>
								{result.ai_analysis
									.split("\n")
									.filter((l) => l.trim())
									.map((line, i) => (
										<p key={i} style={s.aiPara}>
											{line}
										</p>
									))}
							</div>
						</div>
					)}

					{/* ── Section 5: Day-wise breakdown ── */}
					<div className="admin-form-container-v4">
						<h3 style={s.sectionTitle}>📋 Day-wise Breakdown</h3>
						<p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16, marginTop: -8 }}>
							Select a cohort date from the dropdown to view its D0–D15 performance.
						</p>

						{/* Tabs */}
						<div style={s.tabRow}>
							{[
								{ key: "a", label: "Group A", color: COLOR_A },
								{ key: "b", label: "Group B", color: COLOR_B },
							].map(({ key, label, color }) => (
								<button
									key={key}
									onClick={() => setActiveTab(key)}
									style={{
										...s.tabBtn,
										...(activeTab === key
											? { background: "var(--bg-card, #fff)", borderColor: color, color }
											: s.tabBtnInactive),
									}}
								>
									<GroupBadge letter={label.slice(-1)} color={activeTab === key ? color : "#9CA3AF"} />
									<span style={{ marginLeft: 6 }}>{label}</span>
								</button>
							))}
						</div>

						{activeTab === "a" && (
							<CohortDayWise
								group={result.group_a}
								groupLabel="A"
								color={COLOR_A}
								partners={partners}
								totalUsers={agg_a?.total_users}
							/>
						)}
						{activeTab === "b" && (
							<CohortDayWise
								group={result.group_b}
								groupLabel="B"
								color={COLOR_B}
								partners={partners}
								totalUsers={agg_b?.total_users}
							/>
						)}
					</div>
				</>
			)}
		</div>
	);
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = {
	sectionTitle: {
		margin: "0 0 var(--spacing-lg) 0",
		fontSize: 16,
		fontWeight: 600,
		color: "var(--text-primary)",
	},
	chartLabel: {
		fontSize: 13,
		fontWeight: 600,
		color: "var(--text-secondary)",
		marginBottom: 8,
		marginTop: 0,
	},
	pickerRow: {
		display: "flex",
		gap: 24,
		alignItems: "center",
		flexWrap: "wrap",
	},
	pickerCard: {
		flex: 1,
		minWidth: 280,
		background: "var(--bg-secondary, #F9FAFB)",
		border: "1px solid var(--border-color, #E5E7EB)",
		borderRadius: "var(--radius-md, 8px)",
		padding: "var(--spacing-lg, 16px)",
	},
	pickerLabel: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		fontWeight: 600,
		fontSize: 15,
		color: "var(--text-primary)",
		marginBottom: 12,
	},
	dateRow: { display: "flex", gap: 12, flexWrap: "wrap" },
	dateField: {
		flex: 1,
		minWidth: 120,
		display: "flex",
		flexDirection: "column",
		gap: 4,
	},
	fieldLabel: { fontSize: 12, color: "var(--text-muted)", fontWeight: 500 },
	dateInput: {
		padding: "8px 10px",
		border: "1px solid var(--border-color, #D1D5DB)",
		borderRadius: 6,
		fontSize: 14,
		background: "var(--bg-card, #fff)",
		color: "var(--text-primary)",
		outline: "none",
		width: "100%",
		boxSizing: "border-box",
	},
	select: {
		padding: "6px 10px",
		border: "1px solid var(--border-color, #D1D5DB)",
		borderRadius: 6,
		fontSize: 13,
		background: "var(--bg-card, #fff)",
		color: "var(--text-primary)",
		cursor: "pointer",
		outline: "none",
	},
	vsDivider: {
		fontWeight: 700,
		fontSize: 16,
		color: "var(--text-muted)",
		flexShrink: 0,
	},
	compareBtn: {
		padding: "10px 40px",
		background: "var(--primary, #3B82F6)",
		color: "#fff",
		border: "none",
		borderRadius: 8,
		fontSize: 15,
		fontWeight: 600,
		transition: "opacity 0.15s",
	},
	table: { width: "100%", borderCollapse: "collapse", fontSize: 14 },
	th: {
		padding: "12px 16px",
		textAlign: "left",
		background: "var(--bg-secondary, #F3F4F6)",
		fontWeight: 600,
		color: "var(--text-primary)",
		borderBottom: "2px solid var(--border-color, #E5E7EB)",
		whiteSpace: "nowrap",
		verticalAlign: "middle",
	},
	td: {
		padding: "11px 16px",
		color: "var(--text-secondary, #374151)",
		borderBottom: "1px solid var(--border-color, #F3F4F6)",
	},
	rowEven: { background: "var(--bg-card, #fff)" },
	rowOdd: { background: "var(--bg-secondary, #F9FAFB)" },
	aiBox: {
		background: "#FFFBEB",
		border: "1px solid #FCD34D",
		borderRadius: 8,
		padding: "16px 20px",
	},
	aiPara: {
		margin: "0 0 10px 0",
		fontSize: 14,
		lineHeight: 1.7,
		color: "#78350F",
	},
	tabRow: {
		display: "flex",
		gap: 4,
		marginBottom: "var(--spacing-lg)",
		borderBottom: "2px solid var(--border-color, #E5E7EB)",
	},
	tabBtn: {
		display: "inline-flex",
		alignItems: "center",
		padding: "8px 18px",
		border: "2px solid transparent",
		borderBottom: "none",
		borderRadius: "6px 6px 0 0",
		fontSize: 14,
		fontWeight: 600,
		cursor: "pointer",
		background: "transparent",
		transition: "all 0.15s",
		position: "relative",
		bottom: -2,
	},
	tabBtnInactive: { color: "var(--text-muted)" },
	noData: {
		textAlign: "center",
		color: "var(--text-muted)",
		padding: "40px 0",
	},
};

export default CohortComparison;
